/**
 * Ni² My Library Panel — compact, READ-ONLY side rail listing the
 * current surgeon's personally attributable records (cases, reflections,
 * pearls, imaging). Reuses `repository.js` — no new DB tables, no
 * extra API. Every item click routes back through an onOpenRecord
 * callback so the host workspace can either open the record's existing
 * detail view or preload it as context for the composer.
 */
import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Brain,
  Quote,
  Lightbulb,
  Scan,
  FileText,
  Sparkles,
} from "lucide-react";
import {
  getPersonalCases,
  getPersonalReflections,
  getPersonalOperativePearls,
  getPersonalImaging,
} from "@/data/repository";

const SECTIONS = [
  { key: "cases",       label: "My Cases",       Icon: FileText, testId: "my-library-cases" },
  { key: "reflections", label: "My Reflections", Icon: Quote,    testId: "my-library-reflections" },
  { key: "pearls",      label: "My Pearls",      Icon: Lightbulb,testId: "my-library-pearls" },
  { key: "imaging",     label: "Recent Imaging", Icon: Scan,     testId: "my-library-imaging" },
];

function buildIndex() {
  const cases = getPersonalCases().map((c) => ({
    id: c.caseId,
    primary: c.patient?.displayName || c.diagnosisPrimary || c.caseId,
    secondary: [c.approachSurgical, c.diagnosisPrimary].filter(Boolean).join(" · "),
  }));
  const reflections = getPersonalReflections().map((r) => ({
    id: r.reflectionId,
    primary: r.reflectionText || r.textPreview || r.reflectionId,
    secondary: [r.reflectionType, r.caseId].filter(Boolean).join(" · "),
  }));
  const pearls = getPersonalOperativePearls().map((p) => ({
    id: p.operativePearlId,
    primary: p.pearlText || p.operativePearlId,
    secondary: [p.pearlCategory, p.caseId].filter(Boolean).join(" · "),
  }));
  const imaging = getPersonalImaging().map((i) => ({
    id: i.imagingId,
    primary: i.keyFinding || i.filenamePlaceholder || i.imagingId,
    secondary: [i.modality, i.sequence, i.studyDate].filter(Boolean).join(" · "),
  }));
  return { cases, reflections, pearls, imaging };
}

export default function Ni2MyLibraryPanel({
  open,
  onToggle,
  onOpenRecord,
}) {
  const index = useMemo(() => buildIndex(), []);
  const [expanded, setExpanded] = useState({
    cases: true,
    reflections: true,
    pearls: false,
    imaging: false,
  });
  const toggle = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => onToggle && onToggle(true)}
        className="nn-btn nn-btn-ghost self-start"
        data-testid="my-library-open"
        title="Open My Library"
      >
        <Brain className="h-3.5 w-3.5" strokeWidth={1.8} />
        My Library
        <ChevronLeft className="h-3 w-3 opacity-60" strokeWidth={2} />
      </button>
    );
  }

  return (
    <aside
      className="nn-glass nn-scroll flex h-full w-[280px] shrink-0 flex-col overflow-y-auto rounded-2xl"
      style={{ padding: "12px 12px 16px 12px" }}
      data-testid="my-library-panel"
      aria-label="My personal memory library"
    >
      <header className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10.5px] font-medium uppercase tracking-[0.16em] text-[hsl(var(--nn-ink))]">
          <Brain className="h-3.5 w-3.5" strokeWidth={2} style={{ color: "hsl(var(--nn-blue))" }} />
          My Library
        </div>
        <button
          type="button"
          onClick={() => onToggle && onToggle(false)}
          className="nn-btn nn-btn-icon"
          data-testid="my-library-close"
          title="Collapse"
        >
          <ChevronRight className="h-3 w-3" strokeWidth={2} />
        </button>
      </header>

      <p className="mb-2.5 text-[10.5px] leading-[15px] text-[hsl(var(--nn-ink-faint))]">
        Records personally owned by you. Read-only.
      </p>

      {SECTIONS.map((s) => {
        const items = index[s.key] || [];
        const isOpen = expanded[s.key];
        return (
          <section
            key={s.key}
            className="mb-1.5 rounded-lg border border-white/[0.06] bg-white/[0.02]"
            data-testid={s.testId}
          >
            <button
              type="button"
              onClick={() => toggle(s.key)}
              className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left"
              aria-expanded={isOpen}
              data-testid={`${s.testId}-toggle`}
            >
              <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--nn-ink))]">
                <s.Icon
                  className="h-3 w-3"
                  strokeWidth={2}
                  style={{ color: "hsl(var(--nn-blue))" }}
                />
                {s.label}
                <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[9.5px] tracking-normal text-[hsl(var(--nn-ink-faint))]">
                  {items.length}
                </span>
              </span>
              <ChevronDown
                className={
                  "h-3 w-3 transition-transform duration-150 " +
                  (isOpen ? "rotate-180" : "")
                }
                strokeWidth={2}
              />
            </button>
            {isOpen && (
              <ul className="flex flex-col gap-0.5 border-t border-white/[0.05] p-1">
                {items.length === 0 && (
                  <li className="px-2 py-1.5 text-[10.5px] text-[hsl(var(--nn-ink-faint))]">
                    None on record.
                  </li>
                )}
                {items.slice(0, 8).map((it) => (
                  <li key={it.id}>
                    <button
                      type="button"
                      onClick={() => onOpenRecord && onOpenRecord({ kind: s.key, id: it.id })}
                      className="group flex w-full flex-col gap-0.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-white/[0.05]"
                      data-testid={`my-library-item-${it.id}`}
                    >
                      <span className="line-clamp-1 text-[11.5px] text-[hsl(var(--nn-ink))]">
                        {it.primary}
                      </span>
                      <span className="line-clamp-1 text-[10px] uppercase tracking-[0.06em] text-[hsl(var(--nn-ink-faint))]">
                        {it.id}
                        {it.secondary ? ` · ${it.secondary}` : ""}
                      </span>
                    </button>
                  </li>
                ))}
                {items.length > 8 && (
                  <li className="px-2 py-1 text-[9.5px] uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
                    +{items.length - 8} more
                  </li>
                )}
              </ul>
            )}
          </section>
        );
      })}

      <div
        className="mt-auto flex items-center gap-1.5 rounded-lg border border-white/[0.06] px-2 py-1.5 text-[10px] text-[hsl(var(--nn-ink-faint))]"
        style={{ background: "rgba(160,200,255,0.04)" }}
      >
        <Sparkles className="h-3 w-3" strokeWidth={2} style={{ color: "hsl(var(--nn-blue))" }} />
        Click any record to open it in place
      </div>
    </aside>
  );
}
